import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Ban, CalendarClock, Clock, Play, Plus, Search as SearchIcon, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ListTools } from "@/components/ListToolbar";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import type { MassTarget } from "@/components/MassPanel";
import { Badge } from "@/components/ui";
import {
  holdsMaterialReservations,
  listProductionOrders,
  orderTone,
  productionOrderDetailQuery,
  productionOrderListQuery,
  type OrderStatusFilter,
  type ProductionOrderRow,
} from "@/features/production/orders";
import { ProductionOrderPanel } from "@/features/production/ProductionOrderPanel";
import { ProductionOrderMassPanel } from "@/features/production/ProductionOrderMass";
import type { ProductionOrderMassKind } from "@/features/production/mass";
import { validateOrderSearch, type OrderSearch } from "@/features/production/search";
import { activeCompanyOf, useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { todayIso, useFormat } from "@/lib/format";
import { sessionContextQuery, useCan } from "@/lib/session";
import { productionOrderListColumns, productionOrderStatuses } from "@patronix/domain/list";
import { listView, noView, viewQuery } from "@/lib/listView";

// Production orders: list → detail. List = one keyset page per request through
// production_order_page (status filter + search on number / item); detail =
// production_order_document (materials, operations), prefetched on hover.
// Mass operations (release, cancel, reschedule) on the checked rows or the
// whole filter open in the detail area (docs/design/mass-operations.md).

export const Route = createFileRoute("/_app/manufacturing/production-orders")({
  validateSearch: validateOrderSearch,
  loaderDeps: ({ search }) => ({ status: search.status, q: search.q, f: search.f, s: search.s }),
  loader: async ({ context, deps }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionContextQuery);
    const companyId = activeCompanyOf(ctx)?.id;
    if (companyId) await context.queryClient.ensureInfiniteQueryData(productionOrderListQuery(listParams(companyId, deps)));
  },
  component: ProductionOrdersPage,
});

function listParams(companyId: string, s: Pick<OrderSearch, "status" | "q" | "f" | "s">) {
  return { companyId, status: s.status, q: s.q, ...viewQuery(productionOrderListColumns, s) };
}

const filters: OrderStatusFilter[] = ["open", "planned", "released", "in_progress", "completed", "all"];

function ProductionOrdersPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const canManage = useCan("production.order.manage");
  const { company } = useActiveCompany();
  const today = todayIso();
  const setSearch = useCallback(
    (patch: Partial<OrderSearch>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const params = listParams(company?.id ?? "", search);
  // Mass operation open in the detail area (checked rows or the whole filter).
  const [mass, setMass] = useState<{ kind: ProductionOrderMassKind; target: MassTarget; count: number | null } | null>(null);
  const query = useInfiniteQuery(productionOrderListQuery(params));
  const [text, setText] = useState(search.q);
  useEffect(() => {
    if (text === search.q) return;
    const id = setTimeout(() => setSearch({ q: text, po: undefined }), 200);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  const late = (r: ProductionOrderRow) =>
    (r.status === "planned" || r.status === "released" || r.status === "in_progress") && r.due_date < today;
  const columns: Column<ProductionOrderRow>[] = [
    {
      key: "number",
      header: t("so.colOrder"),
      width: "minmax(132px,0.9fr)",
      cell: (r, selected) => <span className={cn("font-mono text-xs", selected && "font-semibold text-accent-ink")}>{r.number}</span>,
    },
    {
      key: "item",
      header: t("bom.colItem"),
      width: "minmax(180px,1.6fr)",
      cell: (r) => (
        <span className="truncate">
          <span className="font-mono text-xs">{r.item_code}</span> <span className="text-ink-soft">{r.item_description}</span>
        </span>
      ),
    },
    {
      key: "status",
      header: t("po.colStatus"),
      width: "minmax(120px,0.9fr)",
      cell: (r) => <Badge tone={orderTone[r.status]}>{t(`status.${r.status}`)}</Badge>,
      options: productionOrderStatuses.map((v) => ({ value: v, label: t(`status.${v}`) })),
    },
    {
      key: "qty",
      header: t("prd.colQty"),
      width: "minmax(120px,0.9fr)",
      align: "end",
      cell: (r) => (
        <span className="tabular">
          {fmt.qty(String(r.reported_good_qty))} / {fmt.qty(String(r.planned_qty))}{" "}
          <span className="text-ink-muted">{r.base_uom_code}</span>
        </span>
      ),
    },
    {
      key: "start",
      header: t("prd.startDate"),
      width: "minmax(104px,0.7fr)",
      cell: (r) => <span className="text-ink-muted">{fmt.day(r.start_date)}</span>,
    },
    {
      key: "due",
      header: t("prd.dueDate"),
      width: "minmax(110px,0.8fr)",
      cell: (r) => (
        <span className={cn("flex items-center gap-1.5", late(r) ? "font-medium text-(--status-danger-fg)" : "text-ink-muted")}>
          {fmt.day(r.due_date)}
          {late(r) && <Clock size={13} strokeWidth={2.2} aria-label={t("po.late")} role="img" />}
        </span>
      ),
    },
    {
      key: "materials",
      header: t("prd.colMaterials"),
      width: "minmax(120px,0.8fr)",
      align: "end",
      cell: (r) =>
        !holdsMaterialReservations(r.status) ? (
          <span className="text-ink-muted">—</span>
        ) : r.short_line_count === 0 ? (
          <span className="text-(--status-success-fg)">{t("so.fullyReserved")}</span>
        ) : (
          <span className="tabular font-medium text-(--status-warning-fg)">{t("so.shortCount", { count: r.short_line_count })}</span>
        ),
    },
  ];
  const exportColumns: ExportColumn<ProductionOrderRow>[] = [
    { header: t("so.colOrder"), kind: "code", value: (r) => r.number },
    { header: t("bom.colItem"), kind: "code", value: (r) => r.item_code },
    { header: t("stock.colDescription"), kind: "text", value: (r) => r.item_description },
    { header: t("po.colStatus"), kind: "code", value: (r) => r.status },
    { header: t("prd.quantity"), kind: "number", value: (r) => r.planned_qty, unit: (r) => r.base_uom_code },
    { header: t("prd.reported"), kind: "number", value: (r) => r.reported_good_qty, unit: (r) => r.base_uom_code },
    { header: t("items.colUom"), kind: "code", value: (r) => r.base_uom_code },
    { header: t("prd.startDate"), kind: "date", value: (r) => r.start_date },
    { header: t("prd.dueDate"), kind: "date", value: (r) => r.due_date },
    { header: t("prd.warehouse"), kind: "code", value: (r) => r.warehouse_code },
    { header: t("so.colShortLines"), kind: "integer", value: (r) => (holdsMaterialReservations(r.status) ? r.short_line_count : null) },
  ];
  const label = (f: OrderStatusFilter) => (f === "open" ? t("po.filterOpen") : f === "all" ? t("po.filterAll") : t(`status.${f}`));

  const listTools: ListTools<ProductionOrderRow> = {
    id: "production-orders",
    title: t("prd.title"),
    onNew: canManage ? () => setSearch({ po: "new" }) : undefined,
    export: { fileBase: "production-orders", sheet: t("prd.title"), columns: exportColumns, fetchPage: (cursor, signal) => listProductionOrders(params, cursor, signal, EXPORT_PAGE) },
    mass:
      canManage && company
        ? (
            [
              { key: "release", label: t("mass.productionOrders.release"), icon: <Play size={15} /> },
              { key: "move", label: t("mass.productionOrders.move"), icon: <CalendarClock size={15} /> },
              { key: "cancel", label: t("mass.productionOrders.cancel"), icon: <Ban size={15} /> },
            ] as const
          ).map((a) => ({
            ...a,
            open: (target: MassTarget, count: number | null) => {
              setSearch({ po: undefined });
              setMass({ kind: a.key, target, count });
            },
          }))
        : [],
  };

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.manufacturing.title")} <span aria-hidden="true">/</span> {t("nav.manufacturing.productionOrders")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("prd.title")}</h1>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setSearch({ po: "new" })}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-accent pl-3 pr-4 font-medium text-on-accent hover:bg-accent-hover"
          >
            <Plus size={18} strokeWidth={2} aria-hidden="true" />
            {t("prd.new")}
          </button>
        )}
      </header>
      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label={t("po.colStatus")} className="flex flex-wrap gap-2">
          {filters.map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={search.status === f}
              onClick={() => setSearch({ status: f, po: undefined })}
              className={cn(
                "h-8 rounded-full border px-3 text-[13px] transition-colors",
                search.status === f
                  ? "border-ink bg-ink font-medium text-surface"
                  : "border-border bg-surface text-ink hover:bg-surface-hover",
              )}
            >
              {label(f)}
            </button>
          ))}
        </div>
        <div className="grow" />
        <label className="flex h-9 min-w-[240px] items-center gap-2 rounded-xl border border-border bg-surface px-3 text-ink-muted focus-within:outline-2 focus-within:outline-focus">
          <SearchIcon size={15} aria-hidden="true" />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("prd.searchPlaceholder")}
            aria-label={t("prd.searchPlaceholder")}
            data-list-search
            className="min-w-0 grow bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-muted"
          />
          {text && (
            <button type="button" aria-label={t("po.clearFilters")} onClick={() => setText("")} className="text-ink-muted">
              <X size={14} />
            </button>
          )}
        </label>
      </div>
      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("prd.listLabel")}
          columns={columns}
          query={query}
          rowId={(r) => r.id}
          selectedId={search.po}
          onSelect={(r) => {
            setMass(null);
            setSearch({ po: r.id });
          }}
          onClear={() => setSearch({ po: undefined })}
          onPrefetch={(r) => void queryClient.prefetchQuery(productionOrderDetailQuery(r.id))}
          emptyTitle={t("prd.emptyTitle")}
          emptyBody={t("po.emptyBody")}
          emptyAction={
            <button
              type="button"
              onClick={() => setSearch({ status: "open", q: "", po: undefined, ...noView })}
              className="h-9 rounded-xl border border-border px-3.5 text-[13px] font-medium hover:bg-surface-hover"
            >
              {t("list.clearFilters")}
            </button>
          }
          minWidth={920}
          tools={listTools}
          view={listView(productionOrderListColumns, search, setSearch)}
        />
        <DetailPanel
          label={mass ? t(`mass.productionOrders.${mass.kind}Title`) : t("prd.detailLabel")}
          open={!!mass || !!search.po}
          placeholder={<DetailPlaceholder title={t("po.selectPrompt")} hint={t("po.selectPromptHint")} />}
        >
          {mass ? (
            <ProductionOrderMassPanel
              key={`${mass.kind}:${mass.target.kind}:${mass.count ?? "all"}`}
              kind={mass.kind}
              target={mass.target}
              count={mass.count}
              list={params}
              onClose={() => setMass(null)}
            />
          ) : search.po && (
            <ProductionOrderPanel
              key={search.po}
              id={search.po}
              onClose={() => setSearch({ po: undefined })}
              onOpen={(id) => setSearch({ po: id })}
            />
          )}
        </DetailPanel>
      </div>
    </div>
  );
}
